import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ProjectFormData } from '../../types';
import projectService from '../../services/projectService';
import { functionalUnitService, FunctionalUnitForSale } from '../../services/functionalUnitService';
import { Search, Building2, Loader2, X, MapPin, Ruler, FileText, User, ArrowLeft } from 'lucide-react';
import { getStatusConfig } from '../Projects/ProjectStatusBadge';
import { WorkStatus, WorkStatusLabels } from '../../types';
import { formatDecimal } from '../../utils/format';

interface UFSelectorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelectUnits: (units: FunctionalUnitForSale[]) => void;
    selectedUnits: FunctionalUnitForSale[]; // Full unit objects, not just IDs
}

type ViewMode = 'projects' | 'units';

const UFSelectorModal: React.FC<UFSelectorModalProps> = ({
    open,
    onOpenChange,
    onSelectUnits,
    selectedUnits
}) => {
    const [viewMode, setViewMode] = useState<ViewMode>('projects');
    const [projects, setProjects] = useState<ProjectFormData[]>([]);
    const [selectedProject, setSelectedProject] = useState<ProjectFormData | null>(null);
    const [units, setUnits] = useState<FunctionalUnitForSale[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSelectedIds, setTempSelectedIds] = useState<number[]>([]);

    // Project filters
    const [projectStatusFilter, setProjectStatusFilter] = useState<string>('all');
    const [projectSortOrder, setProjectSortOrder] = useState<'newest' | 'oldest'>('newest');

    // Unit filters
    const [showSoldUnits, setShowSoldUnits] = useState(false);
    const [unitSortOrder, setUnitSortOrder] = useState<'alphabetic' | 'price-high' | 'price-low'>('alphabetic');

    useEffect(() => {
        if (open) {
            loadProjects();
            // Initialize temp selection with IDs of already selected units
            setTempSelectedIds(selectedUnits.map(u => u.id));
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            // Reset state when closing
            setViewMode('projects');
            setSelectedProject(null);
            setUnits([]);
            setSearchTerm('');
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open, selectedUnits]);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const data = await projectService.getAll();
            setProjects(data);
        } catch (error) {
            console.error('Error loading projects:', error);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const loadUnits = async (projectId: number) => {
        try {
            setLoading(true);
            const data = await functionalUnitService.getFunctionalUnitsForSale(projectId);
            setUnits(data);
        } catch (error) {
            console.error('Error loading units:', error);
            setUnits([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredProjects = projects
        .filter(project => {
            // Search filter
            const matchesSearch = searchTerm === '' ||
                project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (project.client || '').toLowerCase().includes(searchTerm.toLowerCase());

            // Status filter
            const matchesStatus = projectStatusFilter === 'all' ||
                project.projectStatus === projectStatusFilter;

            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            // Sort by updatedAt (newest/oldest)
            const dateA = new Date(a.updatedAt || a.createdAt || new Date()).getTime();
            const dateB = new Date(b.updatedAt || b.createdAt || new Date()).getTime();

            return projectSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

    const filteredUnits = units
        .filter(unit => {
            // Search filter
            const matchesSearch = searchTerm === '' ||
                unit.functionalUnitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                unit.ufType.toLowerCase().includes(searchTerm.toLowerCase());

            // Sold filter
            const matchesSoldFilter = showSoldUnits || !unit.isSale;

            return matchesSearch && matchesSoldFilter;
        })
        .sort((a, b) => {
            switch (unitSortOrder) {
                case 'price-high':
                    return b.listPrice - a.listPrice;
                case 'price-low':
                    return a.listPrice - b.listPrice;
                case 'alphabetic':
                default:
                    return a.functionalUnitName.localeCompare(b.functionalUnitName);
            }
        });

    const handleProjectClick = (project: ProjectFormData) => {
        setSelectedProject(project);
        setSearchTerm('');
        loadUnits(project.id!);
        setViewMode('units');
    };

    const handleBackToProjects = () => {
        setViewMode('projects');
        setSelectedProject(null);
        setUnits([]);
        setSearchTerm('');
    };

    const toggleUnitSelection = (unitId: number) => {
        setTempSelectedIds(prev =>
            prev.includes(unitId)
                ? prev.filter(id => id !== unitId)
                : [...prev, unitId]
        );
    };

    const handleConfirm = () => {
        // Get newly selected units from current project (exclude sold units as safety guard)
        const newlySelectedUnits = units.filter(u => tempSelectedIds.includes(u.id) && !u.isSale);

        // Get units from OTHER projects (not the current one)
        const unitsFromOtherProjects = selectedUnits.filter(
            existingUnit => !units.some(currentProjectUnit => currentProjectUnit.id === existingUnit.id)
        );

        // Merge: keep units from other projects + newly selected from current project
        const mergedUnits = [...unitsFromOtherProjects, ...newlySelectedUnits];

        // Remove duplicates by ID (shouldn't happen, but just in case)
        const uniqueUnits = mergedUnits.filter((unit, index, self) =>
            index === self.findIndex(u => u.id === unit.id)
        );

        onSelectUnits(uniqueUnits);
        onOpenChange(false);
    };

    const handleClose = () => {
        onOpenChange(false);
        setSearchTerm('');
        // Reset temp selection to current selection
        setTempSelectedIds(selectedUnits.map(u => u.id));
    };

    const renderProjectCard = (project: ProjectFormData) => {
        const statusConfig = getStatusConfig(project.projectStatus || WorkStatus.EN_CONSTRUCCION);
        const address = [project.street, project.streetNumber, project.city].filter(Boolean).join(' ') || 'Sin dirección';

        // Count how many selected units belong to this project
        const selectedCount = selectedUnits.filter(u => u.projectName === project.name).length;
        const hasSelections = selectedCount > 0;

        return (
            <div
                key={project.id}
                onClick={() => handleProjectClick(project)}
                className="relative flex flex-col p-4 transition-all border rounded-xl cursor-pointer bg-card border-border hover:border-primary hover:shadow-md group"
            >
                {/* Status Badge - Top Right */}
                <div className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border ${statusConfig.color}`}>
                    <span className={`block h-1.5 w-1.5 rounded-full ${statusConfig.dotColor}`} />
                    <span className="text-[10px] font-medium uppercase tracking-wide">
                        {WorkStatusLabels[project.projectStatus || WorkStatus.EN_CONSTRUCCION]}
                    </span>
                </div>

                {/* Selection Count Badge */}
                {hasSelections && (
                    <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                        {selectedCount} UF
                    </div>
                )}

                {/* Header with Icon and Title */}
                <div className="flex items-start gap-3 mb-3 pr-24">
                    <div className="p-2.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex-shrink-0">
                        <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-foreground transition-colors group-hover:text-primary line-clamp-2">
                            {project.name}
                        </h3>
                    </div>
                </div>

                {/* Project Details */}
                <div className="space-y-2 text-xs">
                    {/* Work Code */}
                    {project.workCode && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">Código: {project.workCode}</span>
                        </div>
                    )}

                    {/* Building Type */}
                    {project.buildingType && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{project.buildingType}</span>
                        </div>
                    )}

                    {/* Total M2 */}
                    {project.totalM2 && project.totalM2 > 0 && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Ruler className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatDecimal(project.totalM2)} m²</span>
                        </div>
                    )}

                    {/* Address */}
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{address}</span>
                    </div>

                    {/* Client */}
                    {project.client && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <User className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{project.client}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderUnitCard = (unit: FunctionalUnitForSale) => {
        const isSelected = tempSelectedIds.includes(unit.id);
        const isSold = unit.isSale;
        const isDisabled = isSold;

        return (
            <div
                key={unit.id}
                onClick={() => !isDisabled && toggleUnitSelection(unit.id)}
                className={`relative flex flex-col bg-card border rounded-xl overflow-hidden transition-shadow ${
                    isDisabled
                        ? 'cursor-not-allowed opacity-60 border-border'
                        : isSelected
                            ? 'cursor-pointer border-primary ring-2 ring-primary/20 shadow-md'
                            : 'cursor-pointer border-border hover:shadow-md hover:border-primary/50'
                }`}
            >
                {/* Header */}
                <div className="flex items-center gap-3 p-4">
                    <div className={`p-2.5 rounded-xl flex-shrink-0 ${
                        isDisabled
                            ? 'bg-muted'
                            : 'bg-indigo-100 dark:bg-indigo-900/30'
                    }`}>
                        <Building2 className={`w-5 h-5 ${
                            isDisabled
                                ? 'text-muted-foreground'
                                : 'text-indigo-600 dark:text-indigo-400'
                        }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate ${
                            isSelected ? 'text-primary' : 'text-foreground'
                        }`}>
                            {unit.functionalUnitName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{unit.ufType}</p>
                    </div>
                </div>

                {/* Status */}
                <div className="mx-4 border-t border-border">
                    <div className="flex items-center justify-between py-2.5">
                        <span className="text-xs text-muted-foreground">Estado</span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-medium ${
                            isSold
                                ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                                : 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
                        }`}>
                            <span className={`block h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                                isSold ? 'bg-red-600 dark:bg-red-400' : 'bg-green-600 dark:bg-green-400'
                            }`} />
                            {isSold ? 'Vendida' : 'Disponible'}
                        </span>
                    </div>
                </div>

                {/* Superficie */}
                <div className="mx-4 border-t border-border">
                    <div className="flex items-center justify-between py-2.5 text-sm">
                        <span className="text-muted-foreground">Superficie total</span>
                        <span className="font-semibold text-foreground">{formatDecimal(unit.totalSurface)} m²</span>
                    </div>
                </div>

                {/* Precio footer */}
                <div className="mt-auto border-t border-border bg-green-50 dark:bg-green-900/20 p-4">
                    <p className="text-xs text-muted-foreground mb-0.5">Precio lista</p>
                    <p className="font-bold text-green-700 dark:text-green-400 text-sm">
                        USD {formatDecimal(unit.listPrice)}
                    </p>
                </div>
            </div>
        );
    };

    const renderLoadingSkeleton = () => {
        return Array.from({ length: 9 }).map((_, index) => (
            <div key={index} role="status" className="flex flex-col border rounded-xl animate-pulse bg-card border-border overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                    <span className="w-10 h-10 rounded-xl bg-muted flex-shrink-0"></span>
                    <div className="flex-1 space-y-2">
                        <div className="h-3 rounded-full bg-muted w-28"></div>
                        <div className="h-2 rounded-full bg-muted w-20"></div>
                    </div>
                </div>
                <div className="mx-4 border-t border-border py-3">
                    <div className="h-2 rounded-full bg-muted w-full"></div>
                </div>
                <div className="border-t border-border p-4">
                    <div className="h-3 rounded-full bg-muted w-24"></div>
                </div>
            </div>
        ));
    };

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative w-full h-full max-w-7xl mx-4 my-4 md:my-8 md:h-[90vh] flex flex-col bg-card rounded-xl shadow-2xl border border-border overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-border bg-muted/30">
                    <div className="flex items-center gap-4 flex-1">
                        {viewMode === 'units' && (
                            <button
                                onClick={handleBackToProjects}
                                className="p-2 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                                aria-label="Volver a proyectos"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        )}
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-foreground">
                                {viewMode === 'projects' ? 'Seleccionar Proyecto' : `${selectedProject?.name}`}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {viewMode === 'projects'
                                    ? 'Seleccione un proyecto para ver sus unidades funcionales'
                                    : 'Seleccione las unidades funcionales que desea agregar'}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                        aria-label="Cerrar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-4 px-6 py-4 border-b border-border bg-background">
                    {viewMode === 'projects' ? (
                        // Project Filters
                        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                            {/* Search */}
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar proyecto..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9"
                                />
                            </div>

                            {/* Status Filter */}
                            <Select value={projectStatusFilter} onValueChange={setProjectStatusFilter}>
                                <SelectTrigger className="w-full sm:w-[180px]">
                                    <SelectValue placeholder="Estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los estados</SelectItem>
                                    <SelectItem value={WorkStatus.PLANIFICACION}>Planificación</SelectItem>
                                    <SelectItem value={WorkStatus.EN_CONSTRUCCION}>En Construcción</SelectItem>
                                    <SelectItem value={WorkStatus.FINALIZADO}>Finalizado</SelectItem>
                                    <SelectItem value={WorkStatus.SUSPENDIDO}>Suspendido</SelectItem>
                                    <SelectItem value={WorkStatus.CANCELADO}>Cancelado</SelectItem>
                                </SelectContent>
                            </Select>

                            {/* Sort Order */}
                            <Select value={projectSortOrder} onValueChange={(value: 'newest' | 'oldest') => setProjectSortOrder(value)}>
                                <SelectTrigger className="w-full sm:w-[180px]">
                                    <SelectValue placeholder="Ordenar por" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">Más reciente</SelectItem>
                                    <SelectItem value="oldest">Más antiguo</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    ) : (
                        // Unit Filters
                        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
                            {/* Search */}
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar unidad funcional..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9"
                                />
                            </div>

                            {/* Sort Order */}
                            <Select value={unitSortOrder} onValueChange={(value: 'alphabetic' | 'price-high' | 'price-low') => setUnitSortOrder(value)}>
                                <SelectTrigger className="w-full sm:w-[200px]">
                                    <SelectValue placeholder="Ordenar por" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alphabetic">Alfabético/Numérico</SelectItem>
                                    <SelectItem value="price-high">Precio: Mayor a Menor</SelectItem>
                                    <SelectItem value="price-low">Precio: Menor a Mayor</SelectItem>
                                </SelectContent>
                            </Select>

                            {/* Show Sold Toggle */}
                            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={showSoldUnits}
                                    onChange={(e) => setShowSoldUnits(e.target.checked)}
                                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                />
                                <span className="text-sm text-foreground">Mostrar vendidas</span>
                            </label>
                        </div>
                    )}
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {loading ? (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {renderLoadingSkeleton()}
                        </div>
                    ) : viewMode === 'projects' ? (
                        filteredProjects.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <div className="flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-muted">
                                    <Building2 className="w-10 h-10 text-muted-foreground" />
                                </div>
                                <h3 className="mb-2 text-xl font-semibold text-foreground">
                                    No se encontraron proyectos
                                </h3>
                                <p className="text-sm text-muted-foreground max-w-md">
                                    {searchTerm
                                        ? 'Intente ajustar sus filtros de búsqueda'
                                        : 'No hay proyectos disponibles'}
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {filteredProjects.map(project => renderProjectCard(project))}
                            </div>
                        )
                    ) : (
                        filteredUnits.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <div className="flex items-center justify-center w-20 h-20 mb-6 rounded-2xl bg-muted">
                                    <Building2 className="w-10 h-10 text-muted-foreground" />
                                </div>
                                <h3 className="mb-2 text-xl font-semibold text-foreground">
                                    No se encontraron unidades funcionales
                                </h3>
                                <p className="text-sm text-muted-foreground max-w-md">
                                    {searchTerm
                                        ? 'Intente ajustar sus filtros de búsqueda'
                                        : 'Este proyecto no tiene unidades funcionales disponibles para la venta'}
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {filteredUnits.map(unit => renderUnitCard(unit))}
                            </div>
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
                    <p className="text-sm text-muted-foreground">
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Cargando...
                            </span>
                        ) : viewMode === 'projects' ? (
                            `${filteredProjects.length} proyecto${filteredProjects.length !== 1 ? 's' : ''} disponible${filteredProjects.length !== 1 ? 's' : ''}`
                        ) : (
                            `${tempSelectedIds.length} unidad${tempSelectedIds.length !== 1 ? 'es' : ''} seleccionada${tempSelectedIds.length !== 1 ? 's' : ''}`
                        )}
                    </p>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={handleClose}>
                            Cancelar
                        </Button>
                        {viewMode === 'units' && tempSelectedIds.length > 0 && (
                            <Button onClick={handleConfirm}>
                                Confirmar ({tempSelectedIds.length})
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UFSelectorModal;
